import React, { useState, useEffect } from 'react';
import { 
  Package, Truck, Clock, MapPin, Phone, TrendingUp, Users, 
  AlertTriangle, CheckCircle, Navigation, User,
  Calendar, BarChart3, Activity, Target, Zap, ArrowUp, ArrowDown,
  Loader2, RefreshCw, Map, Route, Settings, Bell
} from 'lucide-react';

const DispatcherDashboard = ({ shipments, loading, error, onRefresh }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Calculează statisticile pentru dispatcher
  const stats = {
    total: shipments?.length || 0,
    peRuta: shipments?.filter(s => s.status === 'pe_drum').length || 0,
    inAsteptare: shipments?.filter(s => s.status === 'in_asteptare').length || 0,
    incarcare: shipments?.filter(s => s.status === 'in_incarcare').length || 0,
    livrate: shipments?.filter(s => s.status === 'livrat').length || 0,
    urgente: shipments?.filter(s => s.prioritate === 'urgenta' && s.status !== 'livrat').length || 0,
    probleme: shipments?.filter(s => s.status === 'problema' || s.status === 'intarziere').length || 0
  };

  // Statistici operaționale pentru dispatcher
  const soferiActivi = shipments?.length 
    ? [...new Set(shipments
        .filter(s => s.status === 'pe_drum' || s.status === 'in_incarcare')
        .map(s => s.sofer_nume || s.sofer_id))].length
    : 0;

  const vehiculeActive = shipments?.length 
    ? [...new Set(shipments
        .filter(s => s.status === 'pe_drum' || s.status === 'in_incarcare')
        .map(s => s.vehicul_id))].filter(id => id).length
    : 0;

  const expedieriProblematice = shipments?.length 
    ? shipments.filter(s => 
        s.status === 'problema' || 
        s.status === 'intarziere' || 
        (s.prioritate === 'urgenta' && s.status !== 'livrat' && s.progres < 50)
      )
    : [];

  // Expedieri care necesită atenția dispatcherului
  const expedieriAtentie = shipments?.length 
    ? shipments.filter(s => 
        s.status === 'pe_drum' && 
        (s.progres > 90 || !s.vehicul_ultima_pozitie)
      ).slice(0, 6)
    : [];

  // Expedieri care necesită monitorizare
  const expedieriMonitorizare = shipments?.length 
    ? shipments.filter(s => 
        s.status === 'pe_drum' && s.progres > 80
      )
    : [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'pe_drum': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in_asteptare': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'livrat': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'in_incarcare': return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'problema': return 'bg-red-50 text-red-700 border-red-200';
      case 'intarziere': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pe_drum': return 'Pe drum';
      case 'in_asteptare': return 'În așteptare';
      case 'livrat': return 'Livrat';
      case 'in_incarcare': return 'În încărcare';
      case 'problema': return 'Problemă';
      case 'intarziere': return 'Întârziere';
      default: return status;
    }
  };

  const getPriorityIcon = (prioritate) => {
    if (prioritate === 'urgenta') {
      return <Zap className="h-3 w-3 sm:h-4 sm:w-4 text-red-500 animate-pulse" />;
    }
    return null;
  };

  if (loading) {
    return (
      <main className="flex-1 p-3 sm:p-6 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="flex items-center justify-center h-64 sm:h-96">
          <div className="text-center px-4">
            <div className="relative">
              <Loader2 className="h-12 w-12 sm:h-16 sm:w-16 text-blue-600 animate-spin mx-auto mb-4 sm:mb-6" />
              <div className="absolute inset-0 h-12 w-12 sm:h-16 sm:w-16 border-4 border-blue-200 rounded-full animate-pulse mx-auto"></div>
            </div>
            <p className="text-lg sm:text-xl text-gray-700 font-medium">Se încarcă dashboard-ul...</p>
            <p className="text-sm sm:text-base text-gray-500 mt-2">Pregătim datele operaționale</p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 px-3 py-4 sm:p-6 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
     
      

      {/* Statistici operaționale principale */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <div className="group bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Activ</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{stats.total - stats.livrate}</p>
            </div>
          </div>
        </div>

        <div className="group bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Navigation className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Pe Drum</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{stats.peRuta}</p>
            </div>
          </div>
        </div>

        <div className="group bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Așteptare</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{stats.inAsteptare}</p>
            </div>
          </div>
        </div>

        <div className="group bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-white animate-pulse" />
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Urgente</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{stats.urgente}</p>
            </div>
          </div>
        </div>

        <div className="group bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 p-4 sm:p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 col-span-2 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <div className="p-3 sm:p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wide">Șoferi Activi</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{soferiActivi}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alerte și notificări importante */}
      {(stats.urgente > 0 || stats.probleme > 0) && (
        <div className="mb-6 sm:mb-8 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 animate-pulse" />
            <h3 className="text-lg sm:text-xl font-bold text-red-800">⚠️ Atenție Dispatcher</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stats.urgente > 0 && (
              <div className="bg-white/80 rounded-lg p-4 border border-red-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 mb-1">{stats.urgente}</div>
                  <div className="text-sm text-red-700 font-medium">Expedieri urgente</div>
                </div>
              </div>
            )}
            {stats.probleme > 0 && (
              <div className="bg-white/80 rounded-lg p-4 border border-purple-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 mb-1">{stats.probleme}</div>
                  <div className="text-sm text-purple-700 font-medium">Probleme active</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8">
        {/* Expedieri care necesită atenție */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 overflow-hidden hover:shadow-2xl transition-all duration-300">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold">🚨 Necesită Atenție</h3>
              <div className="flex items-center bg-white/20 rounded-full px-2 sm:px-3 py-1">
                <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <span className="text-xs sm:text-sm font-medium">
                  {stats.urgente + expedieriAtentie.length} active
                </span>
              </div>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {/* Expedieri urgente */}
            {shipments?.filter(s => s.prioritate === 'urgenta' && s.status !== 'livrat').map(shipment => (
              <div key={`urgent-${shipment.id}`} className="p-4 sm:p-6 hover:bg-red-50 transition-colors duration-200 border-l-4 border-red-400">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="font-bold text-base sm:text-lg text-gray-900">#{shipment.id}</span>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(shipment.status)}`}>
                      {getStatusText(shipment.status)}
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium animate-pulse">
                      URGENT
                    </span>
                  </div>
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center text-gray-700">
                    <Package className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-red-500 flex-shrink-0" />
                    <span className="font-medium text-sm sm:text-base truncate">{shipment.client}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <User className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
                    <span className="text-sm sm:text-base truncate">{shipment.sofer_nume || 'Nealocat'}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
                    <span className="text-sm sm:text-base">Estimat: {shipment.ora_estimata || 'TBD'}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
                    <span className="text-xs sm:text-sm truncate">{shipment.ruta}</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Expedieri aproape de finalizare */}
            {expedieriAtentie.map(shipment => (
              <div key={`atentie-${shipment.id}`} className="p-4 sm:p-6 hover:bg-blue-50 transition-colors duration-200 border-l-4 border-blue-400">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="font-bold text-base sm:text-lg text-gray-900">#{shipment.id}</span>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(shipment.status)}`}>
                      {getStatusText(shipment.status)}
                    </span>
                    <span className="text-xs text-blue-600 font-medium">
                      {parseInt(shipment.progres) || 0}%
                    </span>
                  </div>
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <div className="flex items-center text-gray-700">
                    <Package className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-blue-500 flex-shrink-0" />
                    <span className="font-medium text-sm sm:text-base truncate">{shipment.client}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <User className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
                    <span className="text-sm sm:text-base truncate">{shipment.sofer_nume}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-2 sm:mr-3 text-gray-400 flex-shrink-0" />
                    <span className="text-xs sm:text-sm truncate">{shipment.ruta}</span>
                  </div>
                  {shipment.progres && (
                    <div className="mt-2 sm:mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-indigo-500 h-1.5 sm:h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${parseInt(shipment.progres) || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {stats.urgente === 0 && expedieriAtentie.length === 0 && (
              <div className="p-6 sm:p-8 text-center">
                <div className="p-3 sm:p-4 bg-green-100 rounded-full w-fit mx-auto mb-3 sm:mb-4">
                  <CheckCircle className="h-8 w-8 sm:h-12 sm:w-12 text-green-500" />
                </div>
                <p className="text-base sm:text-lg font-medium text-gray-900 mb-2">Totul funcționează perfect!</p>
                <p className="text-sm sm:text-base text-gray-600">Nu există expedieri care necesită atenție imediată</p>
              </div>
            )}
          </div>
        </div>

        {/* Monitor operațional */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 overflow-hidden hover:shadow-2xl transition-all duration-300">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold">📊 Monitor Operațional</h3>
              <Activity className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-2 gap-6 sm:gap-8">
              <div className="text-center">
                <div className="p-4 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-lg mb-4 mx-auto w-fit">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-green-600 mb-2">{soferiActivi}</div>
                <p className="text-sm text-gray-600 font-medium">Șoferi Activi</p>
                <p className="text-xs text-gray-500 mt-1">Pe drum + încărcare</p>
              </div>
              <div className="text-center">
                <div className="p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-lg mb-4 mx-auto w-fit">
                  <Truck className="h-8 w-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-purple-600 mb-2">{vehiculeActive}</div>
                <p className="text-sm text-gray-600 font-medium">Vehicule Active</p>
                <p className="text-xs text-gray-500 mt-1">În utilizare</p>
              </div>
            </div>

            {/* Eficiență operațională */}
            <div className="mt-8 space-y-4">
              <div>
                <div className="flex justify-between text-sm font-medium mb-2">
                  <span className="text-gray-600">Capacitate utilizată</span>
                  <span className="text-gray-900">
                    {stats.total ? Math.round(((stats.peRuta + stats.incarcare) / stats.total) * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-3 rounded-full transition-all duration-1000"
                    style={{ width: `${stats.total ? Math.round(((stats.peRuta + stats.incarcare) / stats.total) * 100) : 0}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm font-medium mb-2">
                  <span className="text-gray-600">Completare zilnică</span>
                  <span className="text-gray-900">
                    {stats.total ? Math.round((stats.livrate / stats.total) * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-3 rounded-full transition-all duration-1000"
                    style={{ width: `${stats.total ? Math.round((stats.livrate / stats.total) * 100) : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status overview pentru dispatcher */}
      <div className="mt-6 sm:mt-8 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-lg border border-white/50 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300">
        <div className="mb-6 sm:mb-8">
          <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
            📈 Status Operațional Detailat
          </h3>
          <p className="text-sm sm:text-base text-gray-600">Distribuția expedierilor pe statusuri și priorități</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          {[
            { 
              key: 'inAsteptare', 
              label: 'În Așteptare', 
              gradient: 'from-amber-400 to-amber-600', 
              count: stats.inAsteptare, 
              icon: Clock,
              description: 'Pregătire transport'
            },
            { 
              key: 'incarcare', 
              label: 'În Încărcare', 
              gradient: 'from-orange-400 to-orange-600', 
              count: stats.incarcare, 
              icon: Package,
              description: 'Loading în curs'
            },
            { 
              key: 'peRuta', 
              label: 'Pe Drum', 
              gradient: 'from-blue-400 to-blue-600', 
              count: stats.peRuta, 
              icon: Navigation,
              description: 'Transport activ'
            },
            { 
              key: 'urgente', 
              label: 'Urgente', 
              gradient: 'from-red-400 to-red-600', 
              count: stats.urgente, 
              icon: AlertTriangle,
              description: 'Prioritate maximă'
            },
            { 
              key: 'livrate', 
              label: 'Finalizate', 
              gradient: 'from-green-400 to-green-600', 
              count: stats.livrate, 
              icon: CheckCircle,
              description: 'Livrate cu succes'
            }
          ].map(({ key, label, gradient, count, icon: Icon, description }) => (
            <div key={key} className="group text-center">
              <div className={`relative w-full h-24 sm:h-32 bg-gradient-to-br ${gradient} rounded-xl sm:rounded-2xl mb-3 sm:mb-4 flex flex-col items-center justify-center text-white shadow-lg group-hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1 sm:group-hover:-translate-y-2`}>
                <Icon className="h-6 w-6 sm:h-8 sm:w-8 mb-1 sm:mb-2 group-hover:scale-110 transition-transform duration-300" />
                <span className="text-2xl sm:text-3xl font-bold">{count}</span>
                <div className="absolute inset-0 bg-white/10 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <p className="text-sm sm:text-lg font-bold text-gray-800 mb-1">
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{label.split(' ')[0]}</span>
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mb-1">
                {stats.total ? ((count / stats.total) * 100).toFixed(0) : 0}% din total
              </p>
              <p className="text-xs text-gray-400 hidden sm:block">{description}</p>
            </div>
          ))}
        </div>

        </div>

    
    </main>
  );
};

export default DispatcherDashboard;